import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Container,
  Table,
  Button,
} from "react-bootstrap";
import { useLocation, useSearchParams } from "react-router-dom";

const VahanDetailsPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = () => {
      try {
        setLoading(true);
        setError(null);
        // Try to get full data from sessionStorage
        const fullData = sessionStorage.getItem("vehicleData");
        if (fullData) {
          try {
            const parsedFullData = JSON.parse(fullData);
            // If we already have some data from URL, merge them
            // Otherwise use the full data
            setVehicleData((prev) => ({
              ...prev,
              ...parsedFullData,
            }));
          } catch (parseError) {
            console.error("Error parsing session storage data:", parseError);
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load vehicle details");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [location.state, searchParams]);

  // Loading state
  if (loading) {
    return (
      <Container className="mt-5">
        <Card className="text-center">
          <Card.Body>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <Card.Text className="mt-3">Loading vehicle details...</Card.Text>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container className="mt-5">
        <Card className="text-center border-danger">
          <Card.Body>
            <Card.Text className="text-danger">{error}</Card.Text>
            <Button variant="primary" href="/" className="mt-3">
              Go Back
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // No data state
  if (!vehicleData) {
    return (
      <Container className="mt-5">
        <Card className="text-center">
          <Card.Body>
            <Card.Text className="text-muted">
              No vehicle data available. Please go back and search for a
              vehicle.
            </Card.Text>
            <Button variant="primary" href="/" className="mt-3">
              Go Back
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const {
    regNo,
    vehicleNumber,
    rcExpiryDate,
    status,
    vehicleManufacturerName,
    model,
    owner,
    ownerFatherName,
    vehicleColour,
    vehicleInsuranceUpto,
    puccUpto,
    vehicleTaxUpto,
    regDate,
    bodyType,
    type,
    normsType,
    class: vehicleClass,
    chassis,
    engine,
    vehicleCubicCapacity,
    grossVehicleWeight,
    unladenWeight,
    wheelbase,
    vehicleCategory,
    rtoCode,
    regAuthority,
    ownerCount,
    vehicleInsuranceCompanyName,
    vehicleInsurancePolicyNumber,
    puccNumber,
    permanentAddress,
    presentAddress,
    vehicleSeatCapacity,
    vehicleSleeperCapacity,
    vehicleStandingCapacity,
    vehicleCylindersNo,
    vehicleManufacturingMonthYear,
    rcStandardCap,
    blacklistStatus,
    blacklistDetails,
    challanDetails,
    nocDetails,
    nonUseStatus,
    nonUseFrom,
    nonUseTo,
    nationalPermitNumber,
    nationalPermitUpto,
    nationalPermitIssuedBy,
    permitNumber,
    permitType,
    permitIssueDate,
    permitValidFrom,
    permitValidUpto,
    isCommercial,
    mobileNumber,
    rcFinancer,
    statusAsOn,
    // Arrays that might need special handling
    blacklistDetails: blacklistDetailsArray = [],
    challanDetails: challanDetailsArray = [],
  } = vehicleData;

  // Helper function to format empty values
  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    if (Array.isArray(value) && value.length === 0) {
      return "None";
    }
    return value;
  };

  // Get status badge color
  const getStatusBadgeColor = () => {
    switch (status?.toUpperCase()) {
      case "ACTIVE":
        return "success";
      case "EXPIRED":
        return "danger";
      case "PENDING":
        return "warning";
      default:
        return "secondary";
    }
  };

  return (
    <Container className="py-4">
      <Card className="mb-4 shadow">
        <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">Vehicle Registration Details</h4>
        </Card.Header>
        <Card.Body>
          {/* Header Section */}
          <Row className="mb-4">
            <Col md={8}>
              <h2>
                <Badge bg="light" text="dark" className="me-2">
                  {formatValue(vehicleNumber)}
                </Badge>
                <Badge bg={getStatusBadgeColor()}>{formatValue(status)}</Badge>
              </h2>
              <h5 className="text-muted">
                {formatValue(vehicleManufacturerName)} - {formatValue(model)}
              </h5>
              <p className="mb-0">
                <strong>Owner:</strong> {formatValue(owner)} (
                {formatValue(ownerFatherName)})
              </p>
            </Col>
            <Col md={4} className="text-md-end">
              <div className="d-inline-block p-3 border rounded">
                <div className="text-muted small">Registration Date</div>
                <div className="h5 mb-0">{formatValue(regDate)}</div>
              </div>
            </Col>
          </Row>

          {/* Status Cards */}
          <Row className="mb-4">
            <Col md={3} sm={6} className="mb-3">
              <Card className="h-100 border-success">
                <Card.Body className="text-center">
                  <Card.Title className="text-success">RC Expiry</Card.Title>
                  <Card.Text className="h5">
                    {formatValue(rcExpiryDate)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6} className="mb-3">
              <Card className="h-100 border-info">
                <Card.Body className="text-center">
                  <Card.Title className="text-info">Insurance Upto</Card.Title>
                  <Card.Text className="h5">
                    {formatValue(vehicleInsuranceUpto)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6} className="mb-3">
              <Card className="h-100 border-warning">
                <Card.Body className="text-center">
                  <Card.Title className="text-warning">PUC Upto</Card.Title>
                  <Card.Text className="h5">{formatValue(puccUpto)}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6} className="mb-3">
              <Card className="h-100 border-secondary">
                <Card.Body className="text-center">
                  <Card.Title className="text-secondary">Tax Upto</Card.Title>
                  <Card.Text className="h5">
                    {formatValue(vehicleTaxUpto)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabs-like sections using Cards */}
          <Row>
            {/* Vehicle Specifications */}
            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Vehicle Specifications</h5>
                </Card.Header>
                <Card.Body>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Body Type:</strong>
                        </td>
                        <td>{formatValue(bodyType)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Fuel Type:</strong>
                        </td>
                        <td>{formatValue(type)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Norms Type:</strong>
                        </td>
                        <td>{formatValue(normsType)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Class:</strong>
                        </td>
                        <td>{formatValue(vehicleClass)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Color:</strong>
                        </td>
                        <td>
                          <span
                            className="d-inline-block me-2"
                            style={{
                              width: "15px",
                              height: "15px",
                              backgroundColor: vehicleColour?.toLowerCase(),
                              border: "1px solid #ddd",
                            }}
                          />
                          {formatValue(vehicleColour)}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Chassis No:</strong>
                        </td>
                        <td>{formatValue(chassis)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Engine No:</strong>
                        </td>
                        <td>{formatValue(engine)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Cubic Capacity:</strong>
                        </td>
                        <td>{formatValue(vehicleCubicCapacity)} cc</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Cylinders:</strong>
                        </td>
                        <td>{formatValue(vehicleCylindersNo)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {/* Weight & Dimensions */}
            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Weight & Dimensions</h5>
                </Card.Header>
                <Card.Body>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Gross Vehicle Weight:</strong>
                        </td>
                        <td>{formatValue(grossVehicleWeight)} kg</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Unladen Weight:</strong>
                        </td>
                        <td>{formatValue(unladenWeight)} kg</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Wheelbase:</strong>
                        </td>
                        <td>{formatValue(wheelbase)} mm</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Seat Capacity:</strong>
                        </td>
                        <td>{formatValue(vehicleSeatCapacity)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Sleeper Capacity:</strong>
                        </td>
                        <td>{formatValue(vehicleSleeperCapacity)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Standing Capacity:</strong>
                        </td>
                        <td>{formatValue(vehicleStandingCapacity)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>RC Standard Cap:</strong>
                        </td>
                        <td>{formatValue(rcStandardCap)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Owner & Registration Details */}
          <Row>
            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Owner & Registration Details</h5>
                </Card.Header>
                <Card.Body>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Owner Count:</strong>
                        </td>
                        <td>{formatValue(ownerCount)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>RTO Code:</strong>
                        </td>
                        <td>{formatValue(rtoCode)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Registration Authority:</strong>
                        </td>
                        <td>{formatValue(regAuthority)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Vehicle Category:</strong>
                        </td>
                        <td>{formatValue(vehicleCategory)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Manufacturing Date:</strong>
                        </td>
                        <td>{formatValue(vehicleManufacturingMonthYear)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Is Commercial:</strong>
                        </td>
                        <td>
                          <Badge bg={isCommercial ? "warning" : "info"}>
                            {isCommercial ? "Yes" : "No"}
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {/* Insurance & PUC Details */}
            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Insurance & PUC Details</h5>
                </Card.Header>
                <Card.Body>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Insurance Company:</strong>
                        </td>
                        <td>{formatValue(vehicleInsuranceCompanyName)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Policy Number:</strong>
                        </td>
                        <td>{formatValue(vehicleInsurancePolicyNumber)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>PUC Number:</strong>
                        </td>
                        <td>{formatValue(puccNumber)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Address Details */}
          <Row>
            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Permanent Address</h5>
                </Card.Header>
                <Card.Body>
                  <p className="mb-0">{formatValue(permanentAddress)}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Present Address</h5>
                </Card.Header>
                <Card.Body>
                  <p className="mb-0">{formatValue(presentAddress)}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Special Status Sections */}
          {(blacklistStatus || blacklistDetailsArray.length > 0) && (
            <Card className="mb-4 border-danger">
              <Card.Header className="bg-danger text-white">
                <h5 className="mb-0">Blacklist Status</h5>
              </Card.Header>
              <Card.Body>
                <p>
                  <strong>Status:</strong> {formatValue(blacklistStatus)}
                </p>
                {blacklistDetailsArray.length > 0 && (
                  <div>
                    <strong>Blacklist Details:</strong>
                    <ul>
                      {blacklistDetailsArray.map((detail, index) => (
                        <li key={index}>{JSON.stringify(detail)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {nonUseStatus && (
            <Card className="mb-4 border-warning">
              <Card.Header className="bg-warning text-white">
                <h5 className="mb-0">Non-Use Status</h5>
              </Card.Header>
              <Card.Body>
                <Table borderless size="sm">
                  <tbody>
                    <tr>
                      <td>
                        <strong>Status:</strong>
                      </td>
                      <td>{formatValue(nonUseStatus)}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>From:</strong>
                      </td>
                      <td>{formatValue(nonUseFrom)}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>To:</strong>
                      </td>
                      <td>{formatValue(nonUseTo)}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Permit Details */}
          {(nationalPermitNumber || permitNumber) && (
            <Card className="mb-4 border-info">
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">Permit Details</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  {nationalPermitNumber && (
                    <Col md={6}>
                      <h6>National Permit</h6>
                      <Table borderless size="sm">
                        <tbody>
                          <tr>
                            <td>
                              <strong>Number:</strong>
                            </td>
                            <td>{formatValue(nationalPermitNumber)}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Valid Upto:</strong>
                            </td>
                            <td>{formatValue(nationalPermitUpto)}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Issued By:</strong>
                            </td>
                            <td>{formatValue(nationalPermitIssuedBy)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  )}
                  {permitNumber && (
                    <Col md={6}>
                      <h6>Regular Permit</h6>
                      <Table borderless size="sm">
                        <tbody>
                          <tr>
                            <td>
                              <strong>Number:</strong>
                            </td>
                            <td>{formatValue(permitNumber)}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Type:</strong>
                            </td>
                            <td>{formatValue(permitType)}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Issue Date:</strong>
                            </td>
                            <td>{formatValue(permitIssueDate)}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Valid From:</strong>
                            </td>
                            <td>{formatValue(permitValidFrom)}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Valid Upto:</strong>
                            </td>
                            <td>{formatValue(permitValidUpto)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Footer Information */}
          <Card className="mt-4">
            <Card.Body className="text-center text-muted small">
              <p className="mb-1">Status as on: {formatValue(statusAsOn)}</p>
              <p className="mb-0">
                Mobile: {formatValue(mobileNumber)} | RC Financer:{" "}
                {formatValue(rcFinancer)}
              </p>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default VahanDetailsPage;
